<script>
import $ from 'domtastic';
import compareVersions from 'compare-versions';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import eventHub from 'viralstyle-ui/src/eventHub';
import vueMixins from 'viralstyle-ui/src/vueMixins';
import VsIconButton from 'viralstyle-ui/src/VsIconButton';
import VsLayout from 'viralstyle-ui/src/VsLayout';
import { mapState } from 'vuex';

import packageJson from '@/../package.json';
import navigationHelper from 'utils/navigation-helper';
import mixins from 'utils/client-mixins';
import headerSVG from 'images/header_pattern.svg';
import HelpLogo from 'components/HelpLogo';

export default {
  components: {
    VsLayout,
    VsIconButton,
    HelpLogo,
  },
  mixins: [vueMixins, mixins],
  props: {
    ready: [Boolean, String],
  },
  data: () => ({
    navigationHelper,
    headerSVG,
    menuOpen: false,
    redirectTimeout: null,
  }),
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      currentUserRoleInfo: state => state.currentUserRoleInfo,
      processing: state => state.processing,
      currentUserEmail: state => state.currentUserEmail,
      showMyOrdersLink: state => state.showMyOrdersLink,
    }),
    headerBG() {
      return {
        backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(headerSVG)}')`,
      };
    },
  },
  watch: {
    '$route'(to, from) {
      let path = get(from, 'query.r') ? `${from.path}?r=${from.query.r}` : from.path;

      this.$_checkVersion();
      this.scrollToTop();

      this.navigationHelper.routeChanged(this.$route, from.name ? path : '', this.currentUserRoleInfo);
      if (from.name) window.dataLayer.push({ event: 'Page View' });
    },
  },
  created() {
    $(window).on('click', this.$_closeDropdown);
    eventHub.$on('prevent-redirect', this.$_handlePreventRedirect);
    eventHub.$on('confirm-redirect', this.$_handleConfirmRedirect);
  },
  beforeDestroy() {
    $(window).off('click', this.$_closeDropdown);
    eventHub.$off('prevent-redirect', this.$_handlePreventRedirect);
    eventHub.$off('confirm-redirect', this.$_handleConfirmRedirect);
  },
  methods: {
    $_closeDropdown(e) {
      if (e.target !== document.getElementsByClassName('top-nav__dropdown-trigger')[0]) {
        this.menuOpen = false;
      }
    },
    $_handlePreventRedirect() {
      clearTimeout(this.redirectTimeout);
    },
    $_handleConfirmRedirect(e) {
      if (e === 'logout') this.$_performRedirect();
    },
    $_performRedirect() {
      this.$store.dispatch('logout');
    },
    async $_checkVersion() {
      if (process.env.NODE_ENV !== 'development') {
        let response = await this.$store.dispatch('getVersion');

        if (compareVersions(response.version.version, packageJson.version) !== 0) {
          window.location.reload(true);
        }
      }
    },
    path(link) {
      return { name: `${this.navigationHelper.navRoot}${link.capitalize()}` };
    },
    logout() {
      this.redirectTimeout = setTimeout(() => {
        this.$_performRedirect();
      }, 100);

      eventHub.$emit('redirect', 'logout');
    },
    linkPath(link) {
      if (typeof link === 'object') {
        return this.path(link.key);
      } else {
        return this.path(link);
      }
    },
    linkText(link) {
      if (typeof link === 'object') {
        return `${link.text}`;
      } else {
        return link;
      }
    },
    handleHelpClick() {
      this.$intercom.show();
    },
    handleNavigateBack() {
      if (isObject(navigationHelper.backPath)) {
        this.$router.push(navigationHelper.backPath);
      } else {
        this.$router.push({ path: navigationHelper.backPath });
      }
    },
  },
};
</script>

<template lang='pug'>
transition(name='top-nav')
  nav.top-nav(v-if='ready', :style='headerBG')
    .top-nav__back-button
      vs-icon-button(
        v-if='navigationHelper.showBackButton',
        title='Back',
        icon='arrow-left',
        :disabled='processing',
        :processing='processing',
        @click='handleNavigateBack',
      )
    .top-nav__links-container(:class='{ "top-nav__links-container--has-links": navigationHelper.navLinks.length }')
      router-link.top-nav__link(v-for='link in navigationHelper.navLinks', :key='linkText(link)', :to='linkPath(link)') {{linkText(link)}}
    .top-nav__actions
      vs-layout(vert-align-center-xs, no-gutter)
        help-logo(@click='handleHelpClick')
        .top-nav__dropdown
          i.mdi.mdi-menu-down.top-nav__dropdown-trigger(@click='menuOpen = !menuOpen')
          i.mdi.mdi-menu-down.top-nav__dropdown-caret(:class='{ "top-nav__dropdown-caret--visible": menuOpen }')
          .top-nav__dropdown-menu(:class='{ "top-nav__dropdown-menu--open": menuOpen }')
            .top-nav__dropdown-user
              template(v-if='currentUser.email === currentUserEmail')
                h6 {{currentUser.name}}
                p {{currentUser.email}}
              template(v-else)
                h6 {{currentUserEmail}}
            a.vs-link.top-nav__dropdown-item(v-if='showMyOrdersLink', target='_blank', :href='profileUrl')
              span.mdi.mdi-cart
              span &nbsp;My Orders
            a.vs-link.top-nav__dropdown-item(tabindex='0', title='Logout', @click='logout')
              span.mdi.mdi-logout
              span &nbsp;Log Out
</template>

<style>
@import '~import/index';

.top-nav {
  @include col(sm, 12, true);
  @extend %flex;
  @extend %flex--center;
  @extend %flex--middle;
  @extend %flex--wrap;
  border-bottom: 1px solid $borderColor;
  background-color: $white;
  width: calc(100% - 130px);
  order: 3;
  height: auto;
  background-position: center;
  background-size: 300%;

  @include mq(mf-md) {
    height: 100px;
    order: 2;
    padding: 0;
    background-size: 200%;
  }

  @include mq(mf-lg) {
    background-size: 150%;
  }

  @include mq(mf-xl) {
    background-size: 100%;
  }

  &__back-button {
    @extend %flex;
    @extend %flex--middle;
    @extend %flex--md-center;
    @include col(xs, 12);
    @include col(md, 2);
    @include col(xl, 1);
    order: 2;
    display: none;

    @include mq(mf-md) {
      order: 1;
      display: flex;
    }

    >.vs-icon-button {
      margin: 1rem 0;

      @include mq(mf-md) {
        margin: 0;
      }
    }
  }

  &__actions {
    order: 3;
    display: none;
    @extend %text-center;
    @include col(xs, 12);
    @include col(sm, 2);
    @include col(xl, 1);

    @include mq(mf-md) {
      display: block;
    }
  }

  &__links-container {
    @extend %flex;
    @extend %flex--wrap;
    @extend %flex--row;
    @extend %flex--center;
    @extend %flex--middle;
    @include col(sm, 8);
    @include col(xl, 10);
    order: 1;

    @include mq(mf-md) {
      order: 2;
    }

    &--has-links {
      margin: 1rem 0;

      @include mq(mf-md) {
        margin: 0;
      }
    }
  }

  &__link {
    @extend %transition-200;
    @extend %letter-spacing-1;
    @extend %text-transform;
    color: $topNavFontColor;
    background-color: $white;
    border: 2px solid $borderColor;
    border-radius: 40px;
    margin: .3rem;
    padding: .5rem 1.5rem;

    &.router-link-active,
    &:hover {
      color: $white;
      background-color: $primaryColor;
      border-color: $primaryColor;
    }

    @include mq(mf-md) {
      margin: .2rem;
    }

    @include mq(mf-lg) {
      margin: 5px;
      padding: 7.5px 17.5px;
    }

    @include mq(mf-xl) {
      margin: 5px 11px;
    }

    &--disabled {
      cursor: not-allowed;
      opacity: .6;
    }
  }

  &__dropdown {
    @extend %display-inline-block;
    @extend %position-relative;
    margin-left: .5rem;
    font-size: $h3;
    color: $primaryColor;
    cursor: pointer;
    user-select: none;
  }

  &__dropdown-menu {
    @extend %position-absolute;
    @extend %border;
    @extend %text-left;
    font-size: $h6;
    right: -5px;
    top: 50px;
    min-width: 150px;
    background: $white;
    z-index: 50;
    display: none;

    &--open {
      display: block;
    }
  }

  &__dropdown-item {
    display: block;
    padding: 1rem;

    >span {
      @extend %display-inline-block;
      vertical-align: middle;
    }

    >.mdi {
      margin-right: .25rem;
    }
  }

  &__dropdown-caret {
    @extend %position-absolute;
    color: $borderColor;
    left: 0;
    top: 33px;
    transform: rotate(180deg);
    display: none;

    &--visible {
      display: block;
    }
  }

  &__dropdown-user {
    color: $textColor;
    padding: 1rem;
    border-bottom: 1px solid $borderColor;
    line-height: 1.25;

    >p {
      color: $textAccentColor;
    }
  }

  @include mq(mf-md) {

    &-enter-active,
    &-leave-active {
      transition: all 500ms;
    }

    &-enter,
    &-leave-to {
      transform: translateY(-100px);
    }

    &-enter-to,
    &-leave {
      transform: translateY(0px);
    }
  }
}
</style>